/**
 * AddDataDropdown - Dropdown menu for adding data to chat
 *
 * Options:
 * - Upload files or images
 * - Choose from Data catalog
 * - Human expert interview
 * - Attach a board
 * - Use connectors
 */

import { Upload, Folder, LayoutGrid, Link2, ChevronRight } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from '@/i18n'
import { isAramcoEnv } from '@/config/environment'

interface AddDataDropdownProps {
  onUploadFiles: () => void
  onDataCatalog: () => void
  onClose: () => void
}

interface MenuItemProps {
  icon: React.ReactNode
  label: string
  onClick: () => void
  hasSubmenu?: boolean
  disabled?: boolean
  disabledTooltip?: string
}

function MenuItem({ icon, label, onClick, hasSubmenu = false, disabled = false, disabledTooltip }: MenuItemProps) {
  return (
    <div className="relative group">
      <button
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className={clsx(
          'w-full flex items-center gap-3 px-4 py-3 text-left',
          disabled
            ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors'
        )}
      >
        <span className={disabled ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400 dark:text-gray-500'}>{icon}</span>
        <span className="flex-1 text-sm">{label}</span>
        {hasSubmenu && (
          <ChevronRight className={clsx('h-4 w-4', disabled ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400 dark:text-gray-500')} />
        )}
      </button>
      {/* Tooltip for disabled items */}
      {disabled && disabledTooltip && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-800 dark:bg-gray-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-30">
          {disabledTooltip}
        </div>
      )}
    </div>
  )
}

export function AddDataDropdown({
  onUploadFiles,
  onDataCatalog,
  onClose,
}: AddDataDropdownProps) {
  const { t } = useTranslation()
  const handleClick = (action: () => void) => {
    action()
    onClose()
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-10"
        onClick={onClose}
      />

      {/* Dropdown menu */}
      <div className="absolute bottom-full left-0 mb-2 w-64 bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-xl shadow-lg z-20 overflow-hidden">
        <MenuItem
          icon={<Upload className="h-5 w-5" />}
          label={t('chatv2:add_data.upload', { defaultValue: 'Upload files or images' })}
          onClick={() => handleClick(onUploadFiles)}
        />
        <MenuItem
          icon={<Folder className="h-5 w-5" />}
          label={t('chatv2:add_data.choose_catalog', { defaultValue: 'Choose from Data Catalog' })}
          onClick={() => handleClick(onDataCatalog)}
        />
        {/* Divider */}
        <div className="border-t border-gray-100 dark:border-gray-800 my-1" />

        <MenuItem
          icon={<LayoutGrid className="h-5 w-5" />}
          label={t('chatv2:add_data.attach_board', { defaultValue: 'Attach a board' })}
          onClick={() => {}}
          disabled
          disabledTooltip={isAramcoEnv ? undefined : t('chatv2:add_data.coming_soon', { defaultValue: 'Coming soon' })}
        />
        <MenuItem
          icon={<Link2 className="h-5 w-5" />}
          label={t('chatv2:add_data.use_connectors', { defaultValue: 'Use connectors' })}
          onClick={() => {}}
          disabled
          disabledTooltip={isAramcoEnv ? undefined : t('chatv2:add_data.coming_soon', { defaultValue: 'Coming soon' })}
          hasSubmenu
        />
      </div>
    </>
  )
}

export default AddDataDropdown
